import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import AIFeaturesSection from '@/components/landing/AIFeaturesSection';
import ModulesSection from '@/components/landing/ModulesSection';

export default function LandingPage() {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-sm border-b border-gray-100">
        <div className="container mx-auto px-4 py-4">
          <nav className="flex items-center justify-between">
            <div className="flex items-center">
              <img src="/qpulse-logo.png" alt="QPulseMES" className="w-20 h-20 object-contain" />
            </div>
            <div className="flex items-center space-x-3">
              <Link to="/login">
                <Button variant="ghost" className="text-gray-600 hover:text-gray-900">
                  {t('auth.login')}
                </Button>
              </Link>
              <Link to="/signup">
                <Button className="bg-blue-600 hover:bg-blue-700 shadow-sm">
                  {t('auth.signup')}
                </Button>
              </Link>
            </div>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <main className="relative overflow-hidden">
        <div className="container mx-auto px-4 pt-20 pb-32">
          <div className="text-center max-w-5xl mx-auto relative z-10">
            <div className="inline-flex items-center px-4 py-2 bg-blue-50 text-blue-700 rounded-full text-sm font-medium mb-8">
              🚀 Monitoreo de Producción en Tiempo Real
            </div>
            <h1 className="text-6xl md:text-7xl font-bold text-gray-900 mb-8 leading-tight">
              Transforma tu 
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-purple-600 to-emerald-500"> producción</span> con datos inteligentes
            </h1>
            <p className="text-xl text-gray-600 mb-12 max-w-3xl mx-auto leading-relaxed">
              QPulseMES conecta planeación, ejecución de planta e indicadores en una sola
              plataforma — con inteligencia artificial integrada para ayudarte a decidir
              mejor, más rápido.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
              <Link to="/signup">
                <Button size="lg" className="px-8 py-4 text-lg bg-blue-600 hover:bg-blue-700 shadow-lg hover:shadow-xl transition-all">
                  Comenzar Gratis →
                </Button>
              </Link>
              <Link to="/demo-corp/login">
                <Button variant="outline" size="lg" className="px-8 py-4 text-lg border-gray-300 hover:border-gray-400 transition-all">
                  Ver Demo en Vivo
                </Button>
              </Link>
            </div>
            <div className="text-sm text-gray-500">
              ✓ Instalación en 15 minutos   ✓ Integración con ERPs   ✓ Soporte 24/7
            </div>
          </div>
          
          {/* Background decorations */}
          <div className="absolute top-20 right-10 w-32 h-32 bg-gradient-to-br from-blue-400/20 to-purple-400/20 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 left-10 w-24 h-24 bg-gradient-to-br from-emerald-400/20 to-blue-400/20 rounded-full blur-2xl"></div>
        </div>
      </main>

      {/* Problems Section */}
      <section className="bg-gray-50 py-24">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Los problemas que enfrentas cada día
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              La mayoría de plantas de manufactura pierden millones al año por ineficiencias evitables
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100">
              <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mb-6">
                <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 14.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Tiempo de inactividad imprevisto</h3>
              <p className="text-gray-600">
                Paros no planificados cuestan hasta <strong>$50,000 por hora</strong> y son difíciles de predecir sin datos en tiempo real.
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100">
              <div className="w-16 h-16 bg-orange-100 rounded-2xl flex items-center justify-center mb-6">
                <svg className="w-8 h-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Visibilidad limitada</h3>
              <p className="text-gray-600">
                Sin visibilidad en tiempo real de tu producción, desperdicios y cuellos de botella que afectan <strong>30% de la productividad</strong>.
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100">
              <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center mb-6">
                <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Cumplimiento manual</h3>
              <p className="text-gray-600">
                Procesos manuales de cumplimiento aumentan riesgo de <strong>multas regulatorias</strong> y errores humanos costosos.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Solutions Section */}
      <section className="py-24">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              QPulseMES: La solución integral
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Una plataforma unificada que conecta, optimiza y automatiza toda tu operación
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-blue-600 rounded-3xl flex items-center justify-center mx-auto mb-6">
                <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Monitoreo de Producción en Tiempo Real</h3>
              <p className="text-gray-600 mb-6">
                Dashboards en vivo con el estatus de cada orden, carga por equipo y actividad
                reciente — detecta cuellos de botella antes de que se conviertan en paros costosos.
              </p>
              <div className="bg-blue-50 p-4 rounded-lg">
                <span className="text-lg font-bold text-blue-600">Actualización en tiempo real</span>
              </div>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-3xl flex items-center justify-center mx-auto mb-6">
                <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Automatización Inteligente</h3>
              <p className="text-gray-600 mb-6">
                Usa inteligencia artificial para sugerir la mejor programación de recursos —
                tú siempre revisas y apruebas antes de aplicar cualquier cambio.
              </p>
              <div className="bg-emerald-50 p-4 rounded-lg">
                <span className="text-lg font-bold text-emerald-600">Impulsado por IA (Gemini)</span>
              </div>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-purple-600 rounded-3xl flex items-center justify-center mx-auto mb-6">
                <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Cumplimiento Automatizado</h3>
              <p className="text-gray-600 mb-6">
                Trazabilidad completa, auditorías automáticas y reportes regulatorios. 
                Mantén el cumplimiento sin esfuerzo manual.
              </p>
              <div className="bg-purple-50 p-4 rounded-lg">
                <span className="text-lg font-bold text-purple-600">Bitácora de auditoría inmutable</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <AIFeaturesSection />
      <ModulesSection />

      {/* Social Proof Section */}
      <section className="bg-gray-50 py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Una plataforma completa, lista para tu planta
            </h2>
            <p className="text-lg text-gray-600">
              Todo lo que necesitas para operar, en un solo lugar
            </p>
          </div>
          
          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center bg-white p-6 rounded-2xl shadow-sm">
              <div className="text-4xl font-bold text-blue-600 mb-2">15</div>
              <p className="text-gray-600 text-sm font-medium">Módulos integrados</p>
              <p className="text-gray-500 text-xs mt-1">Producción, planeación y administración</p>
            </div>
            <div className="text-center bg-white p-6 rounded-2xl shadow-sm">
              <div className="text-4xl font-bold text-emerald-600 mb-2">4</div>
              <p className="text-gray-600 text-sm font-medium">Funciones con IA</p>
              <p className="text-gray-500 text-xs mt-1">Impulsadas por Google Gemini</p>
            </div>
            <div className="text-center bg-white p-6 rounded-2xl shadow-sm">
              <div className="text-4xl font-bold text-purple-600 mb-2">100%</div>
              <p className="text-gray-600 text-sm font-medium">Trazabilidad</p>
              <p className="text-gray-500 text-xs mt-1">Bitácora de auditoría inmutable</p>
            </div>
            <div className="text-center bg-white p-6 rounded-2xl shadow-sm">
              <div className="text-4xl font-bold text-orange-600 mb-2">RBAC</div>
              <p className="text-gray-600 text-sm font-medium">Multi-tenant</p>
              <p className="text-gray-500 text-xs mt-1">Permisos granulares por rol</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-24 bg-gradient-to-br from-blue-600 via-blue-700 to-purple-700 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-10 left-10 w-40 h-40 bg-white/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-32 h-32 bg-emerald-400/20 rounded-full blur-2xl"></div>
        </div>
        
        <div className="container mx-auto px-4 text-center relative z-10">
          <h2 className="text-5xl font-bold text-white mb-6">
            ¿Listo para revolucionar tu manufactura?
          </h2>
          <p className="text-xl text-blue-100 mb-12 max-w-2xl mx-auto">
            Empieza tu transformación digital hoy y conecta tu operación en una sola
            plataforma.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <Link to="/signup">
              <Button size="lg" className="px-8 py-4 text-lg bg-white text-blue-600 hover:bg-gray-50 shadow-xl">
                Comenzar Prueba Gratuita →
              </Button>
            </Link>
            <Link to="/demo-corp/login">
              <Button size="lg" variant="outline" className="px-8 py-4 text-lg border-2 border-white text-white hover:bg-white hover:text-blue-600">
                Ver Demo en Vivo
              </Button>
            </Link>
          </div>
          
          <div className="text-blue-100 text-sm">
            ✓ Sin compromisos  ✓ Setup en 15 minutos  ✓ Soporte completo incluido
          </div>
        </div>
      </section>

      {/* Demo Credentials */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-md mx-auto bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
            <div className="text-center mb-6">
              <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 7a2 2 0 012 2m2 0a4 4 0 01-4 4m-2-8a6 6 0 016 6m-6-6a6 6 0 00-6 6m6-6v10m-6-6a6 6 0 00-6 6m6-6H3" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Demo en Vivo</h3>
              <p className="text-gray-600 text-sm">Explora QPulseMES con datos reales</p>
            </div>
            
            <Link to="/demo-corp/login">
              <Button className="w-full bg-emerald-600 hover:bg-emerald-700">
                Acceder al Demo →
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-100">
        <div className="container mx-auto px-4 py-12">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="flex items-center space-x-3 mb-4 md:mb-0">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl flex items-center justify-center">
                <span className="text-white font-bold text-lg">Q</span>
              </div>
              <div>
                <span className="text-xl font-bold text-gray-900">QPulseMES</span>
                <p className="text-sm text-gray-500">Manufacturing Excellence System</p>
              </div>
            </div>
            
            <div className="text-center md:text-right">
              <p className="text-gray-600 mb-1">© 2025 QPulseMES. Todos los derechos reservados.</p>
              <p className="text-sm text-gray-500">Potenciando la manufactura del futuro</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}